/**
 * Ventas — pantalla unificada de venta de productos / vehículos.
 *
 * Centro de operaciones de las ventas: dos tarjetas de acción grandes para
 * registrar una venta ("Venta a crédito" ares2010 / "Venta al contado"
 * ares2015, formularios existentes en diálogos a pantalla completa) y, como
 * listado, el informe de Motores Facturados embebido (mismo componente que
 * el reporte: filtros por fecha/sucursal/tipo/marca/modelo/color, totales,
 * export PDF y eliminación de facturas con permiso de borrado).
 *
 * Sustituye las entradas de menú de ventaproductos.js, ventacontado.js y
 * "Motores Facturados (Nuevo)"; los tres componentes se reutilizan.
 */
import React, { useState, useEffect } from "react";
import useAccionInicial from "lib/useAccionInicial";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import CreditScoreOutlinedIcon from "@mui/icons-material/CreditScoreOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import VentaProductos from "modules/facturacion/ventaproductos";
import VentaContado from "modules/facturacion/ventacontado";
import InformeMotoresFacturadosNuevo from "modules/facturacion/informemotoresfacturados-nuevo";
import { leerBorrador, borrarBorrador } from "lib/borradorventa";
import AvisoBorradorVenta from "components/borradorventa/AvisoBorradorVenta";

export default function Ventas() {
  const [controller] = useMaterialUIController();
  const { roleDetalles, usuarioLog } = controller;

  // Permisos: cada tarjeta se muestra según el programa ARES del rol
  // (el backend re-valida en permisos.py).
  const puedeCredito = tienePermisosModulo(roleDetalles, "ares2010");
  const puedeContado = tienePermisosModulo(roleDetalles, "ares2015");

  const [creditoOpen, setCreditoOpen] = useState(false);
  const [contadoOpen, setContadoOpen] = useState(false);
  // "Continuar" desde el aviso: el formulario abre con el borrador ya
  // restaurado (ver lib/borradorventa).
  const [restaurarCredito, setRestaurarCredito] = useState(false);
  const [restaurarContado, setRestaurarContado] = useState(false);

  // Ventas sin terminar de este usuario en este navegador. Se releen al
  // cerrar cualquiera de los dos formularios.
  const [borradores, setBorradores] = useState({ credito: null, contado: null });
  useEffect(() => {
    if (creditoOpen || contadoOpen) return;
    setBorradores({
      credito: puedeCredito ? leerBorrador("credito", usuarioLog) : null,
      contado: puedeContado ? leerBorrador("contado", usuarioLog) : null,
    });
  }, [creditoOpen, contadoOpen, usuarioLog, puedeCredito, puedeContado]);
  const descartarBorrador = (tipo) => {
    borrarBorrador(tipo, usuarioLog);
    setBorradores((p) => ({ ...p, [tipo]: null }));
  };
  const abrirCredito = (restaurar = false) => {
    setRestaurarCredito(restaurar);
    setCreditoOpen(true);
  };
  const abrirContado = (restaurar = false) => {
    setRestaurarContado(restaurar);
    setContadoOpen(true);
  };

  // Enlace profundo desde el buscador global (?accion=credito|contado).
  useAccionInicial({
    credito: { permitido: puedeCredito, abrir: () => abrirCredito() },
    contado: { permitido: puedeContado, abrir: () => abrirContado() },
  });

  // Incrementarlo recarga el listado embebido (tras guardar o cerrar una venta).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Ventas"
        subtitle="Registra, consulta y elimina ventas de productos/vehículos a crédito y al contado"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Ventas en pausa (borrador guardado en este navegador) */}
        <AvisoBorradorVenta
          borrador={borradores.credito}
          etiqueta="venta a crédito"
          onContinuar={() => abrirCredito(true)}
          onDescartar={() => descartarBorrador("credito")}
          sx={{ mb: 2 }}
        />
        <AvisoBorradorVenta
          borrador={borradores.contado}
          etiqueta="venta al contado"
          onContinuar={() => abrirContado(true)}
          onDescartar={() => descartarBorrador("contado")}
          sx={{ mb: 2 }}
        />

        {/* Acciones principales: tarjetas grandes para iniciar una venta */}
        {(puedeCredito || puedeContado) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeCredito && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={CreditScoreOutlinedIcon}
                  titulo="Venta a crédito"
                  descripcion="Financiada con inicial, cuotas y préstamo"
                  color={FP.color.accentBlue}
                  onClick={() => abrirCredito()}
                />
              </Grid>
            )}
            {puedeContado && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={PaymentsOutlinedIcon}
                  titulo="Venta al contado"
                  descripcion="El cliente paga el precio completo, sin financiamiento"
                  color={FP.color.success}
                  onClick={() => abrirContado()}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Listado: el mismo informe de Motores Facturados, embebido */}
        <InformeMotoresFacturadosNuevo embedded refreshToken={refreshKey} />
      </Box>

      {/* Venta a crédito (formulario completo a pantalla completa) */}
      <Dialog fullScreen open={creditoOpen} onClose={() => setCreditoOpen(false)}>
        <VentaProductos
          embedded
          restaurarBorrador={restaurarCredito}
          onExit={() => {
            setCreditoOpen(false);
            refrescar();
          }}
          onSaved={refrescar}
        />
      </Dialog>

      {/* Venta al contado (formulario completo a pantalla completa) */}
      <Dialog fullScreen open={contadoOpen} onClose={() => setContadoOpen(false)}>
        <VentaContado
          embedded
          restaurarBorrador={restaurarContado}
          onExit={() => {
            setContadoOpen(false);
            refrescar();
          }}
          onSaved={refrescar}
        />
      </Dialog>
    </Box>
  );
}
